import { useState } from 'react';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import {
  BsCalendar2Check,
  BsCalendar2X,
  BsCalendarRange,
  BsExclamationTriangle,
  BsTrashFill,
} from 'react-icons/bs';
import Swal from 'sweetalert2';
import { DefaultButton } from '../../../components/Button/Button';
import {
  DateMaskInput,
  LabelInput,
  SwitchInput,
} from '../../../components/Input/Input';
import { alertError } from '../../../components/Toast';
import './ContractValidity.scss';

const ContractValidity = ({
  contractHistory = [],
  onSaveContractPeriod,
  onRemoveContractPeriod,
  onDeactivateCompany,
  isAllowedToEdit,
  companyActive,
  onChangeActive,
  loggedUser,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPeriod, setNewPeriod] = useState({
    start_date: '',
    end_date: '',
    notes: '',
  });
  // Date conversion utility
  const convertDateToISO = (dateStr) => {
    // Convert DD/MM/YYYY to YYYY-MM-DD for database
    if (!dateStr || dateStr.includes('_')) return null;
    const [day, month, year] = dateStr.split('/');
    if (!day || !month || !year) return null;
    return `${year}-${month}-${day}`;
  };

  const validateDate = (dateStr) => {
    // Validate DD/MM/YYYY format and check if valid date
    if (!dateStr || dateStr.includes('_')) return false;
    const [day, month, year] = dateStr.split('/');
    if (!day || !month || !year || year.length !== 4) return false;
    const date = new Date(year, month - 1, day);
    return (
      date.getDate() == day &&
      date.getMonth() == month - 1 &&
      date.getFullYear() == year
    );
  };

  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '-';
    // Parse directly without timezone conversion
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    // Parse dates without timezone conversion
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);
    const months = (endYear - startYear) * 12 + (endMonth - startMonth);
    return `${months} ${months === 1 ? 'mês' : 'meses'}`;
  };

  const getPeriodStatus = (startDate, endDate) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    if (!endDate)
      return { status: 'Em Andamento', className: 'status-ongoing' };
    if (endDate >= todayStr && startDate <= todayStr)
      return { status: 'Ativo', className: 'status-active' };
    if (endDate < todayStr)
      return { status: 'Expirado', className: 'status-expired' };
    return { status: 'Futuro', className: 'status-future' };
  };

  const handleSaveNewPeriod = async () => {
    // Validate dates
    if (!validateDate(newPeriod.start_date)) {
      alertError('Data de início inválida');
      return;
    }
    if (newPeriod.end_date && !validateDate(newPeriod.end_date)) {
      alertError('Data de término inválida');
      return;
    }

    // Convert to ISO and save
    const periodToSave = {
      start_date: convertDateToISO(newPeriod.start_date),
      end_date: convertDateToISO(newPeriod.end_date),
      notes: newPeriod.notes,
    };

    const saved = await onSaveContractPeriod(periodToSave);
    if (saved) {
      setShowAddModal(false);
      setNewPeriod({ start_date: '', end_date: '', notes: '' });
    }
  };

  const handleChangePeriodField = (field, value) => {
    setNewPeriod((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeletePeriod = async (period) => {
    const result = await Swal.fire({
      title: 'Excluir Período de Contrato?',
      html: `
        <div style="text-align: left; margin-top: 15px;">
          <p style="margin-bottom: 12px;"><strong>Período:</strong></p>
          <ul style="list-style: none; padding-left: 0;">
            <li style="margin-bottom: 8px;">📅 <strong>Início:</strong> ${formatDateForDisplay(
              period.start_date
            )}</li>
            <li style="margin-bottom: 8px;">📅 <strong>Término:</strong> ${formatDateForDisplay(
              period.end_date
            )}</li>
            ${
              period.notes
                ? `<li style="margin-bottom: 8px;">📝 <strong>Obs:</strong> ${period.notes}</li>`
                : ''
            }
          </ul>
          <p style="margin-top: 15px; color: #f16063;"><strong>⚠️ Esta ação não pode ser desfeita!</strong></p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f16063',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      await onRemoveContractPeriod(period.id);
    }
  };

  // Render contract period card
  const renderPeriodCard = (period) => {
    const statusInfo = getPeriodStatus(period.start_date, period.end_date);
    const duration = calculateDuration(period.start_date, period.end_date);

    return (
      <div
        key={period.id}
        className={`contract-period-card ${statusInfo.className}`}
      >
        <div className="period-card-header">
          <div className="period-status-badge-small">
            <span className={`status-dot ${statusInfo.className}`}></span>
            {statusInfo.status}
          </div>
          {isAllowedToEdit && (
            <BsTrashFill
              className="period-delete-icon"
              onClick={() => handleDeletePeriod(period)}
              title="Excluir período"
            />
          )}
        </div>

        <div className="period-card-body">
          <div className="period-dates">
            <div className="period-date-item">
              <BsCalendarRange className="date-icon" />
              <div className="date-content">
                <span className="date-label">Início</span>
                <span className="date-value">
                  {formatDateForDisplay(period.start_date)}
                </span>
              </div>
            </div>

            <div className="period-separator">→</div>

            <div className="period-date-item">
              <BsCalendarRange className="date-icon" />
              <div className="date-content">
                <span className="date-label">Término</span>
                <span className="date-value">
                  {formatDateForDisplay(period.end_date)}
                </span>
              </div>
            </div>
          </div>

          <div className="period-duration">
            <span className="duration-label">Duração:</span>
            <span className="duration-value">{duration}</span>
          </div>

          {period.notes && (
            <div className="period-notes">
              <span className="notes-label">Observações:</span>
              <span className="notes-value">{period.notes}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Calculate current contract status from history
  const getContractStatus = () => {
    // Se a empresa estiver inativa, retorna status "Inativa" independente do contrato
    if (!companyActive) {
      return {
        status: 'Inativa',
        className: 'status-inactive',
        icon: <BsCalendar2X />,
        description: 'A empresa está desativada no sistema',
      };
    }

    if (!contractHistory || contractHistory.length === 0) {
      return {
        status: 'Não Definido',
        className: 'status-undefined',
        icon: <BsCalendar2X />,
        description: 'Nenhum período de contrato registrado',
      };
    }

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Find active or ongoing period
    const activePeriod = contractHistory.find((period) => {
      if (!period.end_date) return period.start_date <= todayStr; // Ongoing
      return period.start_date <= todayStr && period.end_date >= todayStr; // Active
    });

    if (activePeriod) {
      if (!activePeriod.end_date) {
        return {
          status: 'Em Andamento',
          className: 'status-ongoing',
          icon: <BsCalendar2Check />,
          description: 'Contrato em andamento sem data de término definida',
        };
      }

      // Calculate days remaining
      const endParts = activePeriod.end_date.split('-');
      const todayParts = todayStr.split('-');
      const endDateObj = new Date(endParts[0], endParts[1] - 1, endParts[2]);
      const todayObj = new Date(
        todayParts[0],
        todayParts[1] - 1,
        todayParts[2]
      );
      const daysRemaining = Math.ceil(
        (endDateObj - todayObj) / (1000 * 60 * 60 * 24)
      );
      const isExpiringSoon = daysRemaining <= 30 && daysRemaining > 0;

      return {
        status: 'Ativo',
        className: isExpiringSoon ? 'status-expiring' : 'status-active',
        icon: isExpiringSoon ? <BsExclamationTriangle /> : <BsCalendar2Check />,
        description: isExpiringSoon
          ? `Atenção: O contrato expira em ${daysRemaining} dia${
              daysRemaining !== 1 ? 's' : ''
            }`
          : `Contrato ativo. Vence em ${daysRemaining} dia${
              daysRemaining !== 1 ? 's' : ''
            }`,
      };
    }

    // Check for most recent expired period
    const expiredPeriods = contractHistory
      .filter((period) => period.end_date && period.end_date < todayStr)
      .sort((a, b) => b.end_date.localeCompare(a.end_date));

    if (expiredPeriods.length > 0) {
      const lastExpired = expiredPeriods[0];
      const endParts = lastExpired.end_date.split('-');
      const todayParts = todayStr.split('-');
      const endDateObj = new Date(endParts[0], endParts[1] - 1, endParts[2]);
      const todayObj = new Date(
        todayParts[0],
        todayParts[1] - 1,
        todayParts[2]
      );
      const daysExpired = Math.ceil(
        (todayObj - endDateObj) / (1000 * 60 * 60 * 24)
      );

      return {
        status: 'Expirado',
        className: 'status-expired',
        icon: <BsCalendar2X />,
        description: `O último contrato expirou há ${daysExpired} dia${
          daysExpired !== 1 ? 's' : ''
        }`,
      };
    }

    return {
      status: 'Não Definido',
      className: 'status-undefined',
      icon: <BsCalendar2X />,
      description: 'Nenhum período de contrato ativo encontrado',
    };
  };

  const statusInfo = getContractStatus();
  const isExpired = statusInfo.status === 'Expirado';

  return (
    <div className="contract-validity-container">
      <div className="contract-validity-header">
        <h3 className="contract-validity-title">Vigência do Contrato</h3>
        <p className="contract-validity-subtitle">
          Gerencie o período de validade do contrato da empresa
        </p>
      </div>

      <div className="contract-validity-content">
        {/* Switch Empresa Ativa */}
        {isAllowedToEdit && (
          <div className="contract-company-status">
            <div className="company-status-card">
              <div className="company-status-info">
                <span className="company-status-label">
                  Status da Empresa no Sistema
                </span>
                <span
                  className={`company-status-badge ${
                    companyActive ? 'active' : 'inactive'
                  }`}
                >
                  {companyActive ? '✓ Empresa Ativa' : '✕ Empresa Inativa'}
                </span>
                <span className="company-status-description">
                  {companyActive
                    ? 'A empresa está ativa e usuários podem acessar o sistema'
                    : 'A empresa está inativa e usuários não podem acessar o sistema'}
                </span>
              </div>
              <div className="company-status-switch">
                <SwitchInput
                  value={companyActive}
                  onChange={onChangeActive}
                  label={companyActive ? 'Desativar Empresa' : 'Ativar Empresa'}
                />
              </div>
            </div>
          </div>
        )}

        {/* Status do Contrato - Calculado automaticamente do histórico */}
        <div className={`contract-status-card ${statusInfo.className}`}>
          <div className="contract-status-icon">{statusInfo.icon}</div>
          <div className="contract-status-info">
            <span className="contract-status-label">
              Status do Contrato (Calculado Automaticamente)
            </span>
            <span className="contract-status-value">{statusInfo.status}</span>
            <span className="contract-status-description">
              {statusInfo.description}
            </span>
          </div>
        </div>

        {/* Alerta de Contrato Expirado */}
        {isExpired && (
          <div className="contract-expired-alert">
            <div className="contract-expired-icon">
              <BsExclamationTriangle />
            </div>
            <div className="contract-expired-content">
              <h4 className="contract-expired-title">
                ⚠️ Atenção: Contrato Expirado
              </h4>
              <p className="contract-expired-message">
                O último período de contrato desta empresa expirou. Para
                bloquear o acesso ao sistema, desative a empresa usando o switch
                "Status da Empresa no Sistema" acima, ou adicione um novo
                período de contrato.
              </p>
            </div>
          </div>
        )}

        {/* Contract History Section */}
        <div className="contract-history-section">
          <div className="contract-history-header">
            <h4 className="contract-history-title">Histórico de Contratos</h4>
            {isAllowedToEdit && (
              <DefaultButton
                label="Adicionar Período"
                onClick={() => setShowAddModal(true)}
                className="add-period-button"
              />
            )}
          </div>

          <div className="contract-history-cards">
            {contractHistory && contractHistory.length > 0 ? (
              contractHistory.map((period) => renderPeriodCard(period))
            ) : (
              <div className="no-history-message">
                <BsCalendar2X className="no-history-icon" />
                <p>Nenhum período de contrato registrado</p>
                {isAllowedToEdit && (
                  <span className="no-history-hint">
                    Clique em "Adicionar Período" para registrar o primeiro
                    período
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Informações Adicionais */}
        <div className="contract-info-box">
          <h4 className="contract-info-title">ℹ️ Como Funciona</h4>
          <ul className="contract-info-list">
            <li>
              <strong>Status Automático:</strong> O status do contrato é
              calculado automaticamente com base nos períodos cadastrados no
              histórico
            </li>
            <li>
              <strong>Período Ativo:</strong> Se houver um período com data
              início ≤ hoje ≤ data término, o contrato está ativo
            </li>
            <li>
              <strong>Período em Andamento:</strong> Se um período não tiver
              data de término definida, ele é considerado em andamento
            </li>
            <li>
              <strong>Status da Empresa:</strong> Controla efetivamente o acesso
              ao sistema. Se desativada, nenhum usuário consegue fazer login
              (independente do status do contrato)
            </li>
            <li>
              <strong>Histórico Completo:</strong> Registra todos os períodos
              contratuais, incluindo renovações, extensões e períodos passados
            </li>
          </ul>
        </div>
      </div>

      {/* Add Period Modal */}
      {showAddModal && (
        <div
          className="contract-modal-overlay"
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="contract-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contract-modal-header">
              <div className="contract-modal-header-title">
                <BsCalendarRange className="contract-modal-header-icon" />
                <h4>Adicionar Período de Contrato</h4>
              </div>
              <div className="contract-modal-header-buttons">
                <DefaultButton
                  className="contract-modal-button-save"
                  label="Salvar"
                  icon={<AiOutlineCheck />}
                  onClick={handleSaveNewPeriod}
                />
                <DefaultButton
                  className="contract-modal-button-cancel"
                  label="Cancelar"
                  icon={<AiOutlineClose />}
                  onClick={() => setShowAddModal(false)}
                />
              </div>
            </div>
            <div className="contract-modal-body">
              <DateMaskInput
                label="Data de Início*"
                value={newPeriod.start_date}
                onChange={(value) =>
                  handleChangePeriodField('start_date', value)
                }
                placeholder="DD/MM/AAAA"
              />
              <DateMaskInput
                label="Data de Término"
                value={newPeriod.end_date}
                onChange={(value) => handleChangePeriodField('end_date', value)}
                placeholder="DD/MM/AAAA"
              />
              <LabelInput
                type="textarea"
                className="cellphone-company-input"
                label="Observações"
                value={newPeriod.notes}
                onChange={(value) => handleChangePeriodField('notes', value)}
                placeholder="Informações adicionais sobre este período"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractValidity;
